import { MultiPolygon, Polygon } from 'geojson'
import debounce from 'debounce-promise'
import React, { useCallback, useMemo } from 'react'
import AsyncSelect from 'react-select/async'
import styled from 'styled-components'

import geoListDefinition from '~/packages/geo/list/geoList.definition'
import { spaceTypeSingularLabels } from '~/packages/general/variable/spaceTime.data'
import {
  Layer,
  Space,
  SpaceChildrenGeoStandard,
  SpaceChildrenItem,
  SpaceGeoStandardId,
  SpaceDelimitationGeoStandardWithShape,
  SpaceType,
} from '~/packages/general/variable/spaceTime.types'

import useActionFunction from '~/prix/react/hooks/actionFunction'
import theme from '~/design'
import { SpaceForPoint } from '~/prix/types/presets/space'
import { ToolsAndOptionalResult } from '~/packages/general/variable/tools.typing'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import useAPI from '~/prix/react/hooks/api'
import { EditSpaceToolProps } from '~/components/mapContent/viewSelect'
import {
  buildHealthProfessionalTeamDelimitationKey,
  buildHealthProfessionalUnitDelimitationKey,
  parseHealthProfessionalDelimitationKey,
} from '~/packages/publicHealth/healthProfessional/healthProfessionalDelimitation.shared'

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  max-height: 70vh;
  min-height: auto;

  .delimitation {
    background-color: ${({ theme }) => theme.colors.lightGrey};
    padding: ${({ theme }) => theme.padding(2)} ${({ theme }) => theme.padding(2)};
  }

  .children-option {
    border: none;
    background-color: white;
    width: 100%;
    display: flex;
    position: relative;
    flex-direction: row;
    align-items: center;
    gap: ${({ theme }) => theme.padding(1)};
    padding: ${({ theme }) => theme.padding(1)} ${({ theme }) => theme.padding(2)};
    cursor: pointer;
    font-weight: bold;
  }
`

interface SpaceEditProps extends EditSpaceToolProps {
  layer: Layer
  currentLayer: Layer
  setCurrentLayer: React.Dispatch<React.SetStateAction<Layer>>
  isHighResolution?: boolean
  spacesLimitation?:
    | {
        space: Partial<SpaceForPoint>
      }
    | undefined
}

interface DelimitationOption {
  id: SpaceGeoStandardId
  key?: string | null
  municipalityId?: SpaceGeoStandardId | null
  name: string
  type: string
  subtitle?: string
  shape: Polygon | MultiPolygon | null
}

interface DelimitationOptionWithSearchRanking extends DelimitationOption {
  searchRanking: number
}

function delimitationTypeLabel(type: string) {
  if (type === 'healthUnit') {
    return 'Unidade de saúde'
  }
  if (type === 'healthTeam') {
    return 'Equipe de saúde'
  }
  return spaceTypeSingularLabels[type as SpaceType] || type
}

function sortOptionsByRanking(options: DelimitationOptionWithSearchRanking[]) {
  options.sort((first, second) => {
    const rankingDifference = Number(second.searchRanking) - Number(first.searchRanking)
    if (rankingDifference !== 0) {
      return rankingDifference
    }
    return first.name.localeCompare(second.name, 'pt-BR')
  })
}

export default function SpaceEdit({
  currentLayer,
  setCurrentLayer,
  currentTool,
  spacesLimitation,
  isHighResolution = false,
}: SpaceEditProps) {
  const { context } = useAPI()
  const isHealthTeamTool = currentTool?.key === 'healthTeams'
  const isHealthProfessionalTool = currentTool?.key === 'healthProfessionals'
  const isHealthHierarchyTool = isHealthTeamTool || isHealthProfessionalTool

  const { callAction } = useActionFunction(geoListDefinition)

  const loadMunicipalityOptions = useCallback(
    async (inputValue: string): Promise<DelimitationOption[]> => {
      const municipalityQueryParams: {
        query: string
        limit: number
        include: string[]
        spaces?: Partial<SpaceForPoint>[]
        highResolution?: boolean
      } = {
        query: inputValue.trim(),
        limit: 100,
        include: ['geometry'],
        highResolution: isHighResolution,
      }

      if (spacesLimitation?.space) {
        municipalityQueryParams.spaces = [spacesLimitation.space]
      }

      const municipalityResult = await callAction(municipalityQueryParams).resultPromise
      return municipalityResult.items
        .filter(item => item?.type === 'municipality')
        .map(item => ({
          id: Number(item.id),
          key: null,
          municipalityId: Number(item.id),
          name: item.name ?? '',
          type: 'municipality',
          subtitle: item.subtitle ?? undefined,
          shape: (item.shape as Polygon | MultiPolygon | null) ?? null,
        }))
    },
    [callAction, isHighResolution, spacesLimitation?.space],
  )

  const loadHealthUnitOptionsByMunicipality = useCallback(
    async (inputValue: string, municipalityIds: number[]): Promise<DelimitationOptionWithSearchRanking[]> => {
      const healthUnitOptions = (
        await Promise.all(
          municipalityIds.map(async municipalityId => {
            const response = await callAction({
              query: inputValue.trim(),
              limit: 100,
              include: ['geometry'],
              highResolution: isHighResolution,
              spaces: [
                {
                  delimitationType: 'municipality',
                  delimitationId: municipalityId,
                  childrenType: 'custom',
                  childrenKey: 'healthUnit',
                },
              ],
            }).resultPromise

            return response.items
              .filter(item => item?.type === 'healthUnit' && item.shape)
              .map(item => ({
                id: municipalityId,
                key: (item as { key?: string | null }).key ?? null,
                municipalityId:
                  (item as { municipalityId?: number | null }).municipalityId ?? municipalityId,
                name: item.name ?? '',
                type: 'healthUnit',
                subtitle: item.subtitle ?? undefined,
                shape: (item.shape as Polygon | MultiPolygon | null) ?? null,
                searchRanking: item.searchRanking ?? 0,
              }))
          }),
        )
      ).flat()

      return healthUnitOptions
    },
    [callAction, isHighResolution],
  )

  const loadHealthTeamOptionsByMunicipality = useCallback(
    async (inputValue: string, municipalityIds: number[]): Promise<DelimitationOptionWithSearchRanking[]> => {
      const healthTeamOptions = (
        await Promise.all(
          municipalityIds.map(async municipalityId => {
            const response = await callAction({
              query: inputValue.trim(),
              limit: 100,
              include: ['geometry'],
              highResolution: isHighResolution,
              spaces: [
                {
                  delimitationType: 'municipality',
                  delimitationId: municipalityId,
                  childrenType: 'custom',
                  childrenKey: 'healthTeam',
                },
              ],
            }).resultPromise

            return response.items
              .filter(item => item?.type === 'healthTeam' && item.shape)
              .map(item => ({
                id: municipalityId,
                key: (item as { key?: string | null }).key ?? null,
                municipalityId:
                  (item as { municipalityId?: number | null }).municipalityId ?? municipalityId,
                name: item.name ?? '',
                type: 'healthTeam',
                subtitle: item.subtitle ?? undefined,
                shape: (item.shape as Polygon | MultiPolygon | null) ?? null,
                searchRanking: item.searchRanking ?? 0,
              }))
          }),
        )
      ).flat()

      return healthTeamOptions
    },
    [callAction, isHighResolution],
  )

  const delimitationOptions = async (inputValue: string): Promise<Array<DelimitationOption>> => {
    if (isHealthHierarchyTool) {
      const municipalityOptions = await loadMunicipalityOptions(inputValue)
      const municipalityIds = new Set<number>()
      municipalityOptions.forEach(item => municipalityIds.add(Number(item.id)))

      const currentSpace = currentLayer.space as SpaceDelimitationGeoStandardWithShape
      if (municipalityIds.size === 0 && typeof currentSpace.delimitationId === 'number') {
        municipalityIds.add(currentSpace.delimitationId)
      }

      const municipalityIdsList = Array.from(municipalityIds)
      const healthUnitOptions = await loadHealthUnitOptionsByMunicipality(inputValue, municipalityIdsList)
      const healthTeamOptions = isHealthProfessionalTool
        ? await loadHealthTeamOptionsByMunicipality(inputValue, municipalityIdsList)
        : []

      const allOptions: DelimitationOptionWithSearchRanking[] = [
        ...municipalityOptions.map(option => ({
          ...option,
          searchRanking: 1,
        })),
        ...healthUnitOptions,
        ...healthTeamOptions,
      ]

      sortOptionsByRanking(allOptions)
      return allOptions
    }

    const params: {
      query: string
      limit: number
      include: string[]
      spaces?: Partial<SpaceForPoint>[]
      highResolution?: boolean
    } = {
      query: inputValue.trim(),
      limit: 100,
      include: ['geometry'],
      highResolution: isHighResolution,
    }

    if (spacesLimitation?.space) {
      params.spaces = [spacesLimitation.space]
    }

    const { resultPromise } = callAction(params)
    const result = await resultPromise
    if (result.items.length > 0 && result.items[0].searchRanking) {
      result.items.sort((a, b) => Number(b.searchRanking) - Number(a.searchRanking))
    }
    return result.items as DelimitationOption[]
  }

  const handleSetDelimitationFromSelect = useCallback(
    (item: DelimitationOption | null) => {
      if (!item) {
        return
      }
      if (!context.user) {
        return
      }
      if (!item.shape) {
        return
      }

      const currentSpace = currentLayer.space as SpaceDelimitationGeoStandardWithShape & {
        delimitationKey?: string | null
      }

      setCurrentLayer(oldLayer => {
        const childrenType: SpaceType = oldLayer.space.childrenType
        const childrenTitle = (oldLayer.space as SpaceChildrenGeoStandard).childrenTitle
        const childrenItemView = (oldLayer.space as SpaceChildrenItem).childrenItemView

        let nextDelimitationId = item.id
        let nextDelimitationType = item.type
        let nextDelimitationKey: string | null = null

        if (isHealthTeamTool && item.type === 'healthUnit') {
          nextDelimitationId = item.municipalityId ?? currentSpace.delimitationId
          nextDelimitationType = 'municipality'
          nextDelimitationKey = item.key ?? null
        }

        if (isHealthProfessionalTool) {
          if (item.type === 'healthUnit') {
            nextDelimitationId = item.municipalityId ?? currentSpace.delimitationId
            nextDelimitationType = 'municipality'
            nextDelimitationKey = item.key
              ? buildHealthProfessionalUnitDelimitationKey(item.key)
              : null
          } else if (item.type === 'healthTeam') {
            nextDelimitationId = item.municipalityId ?? currentSpace.delimitationId
            nextDelimitationType = 'municipality'
            nextDelimitationKey = item.key
              ? buildHealthProfessionalTeamDelimitationKey(item.key)
              : null
          } else {
            nextDelimitationType = 'municipality'
          }
        }

        const space = {
          ...oldLayer.space,
          childrenTitle,
          childrenItemView,
          childrenType,
          childrenH3Resolution: undefined,
          delimitationId: nextDelimitationId,
          delimitationTitle: item.name,
          delimitationType: nextDelimitationType,
          delimitationKey: nextDelimitationKey,
          shape: item.shape,
        } as Space & {
          shape: Polygon | MultiPolygon
          delimitationKey?: string | null
        }

        return {
          ...oldLayer,
          space,
        }
      })
    },
    [context.user, currentLayer.space, isHealthProfessionalTool, isHealthTeamTool, setCurrentLayer],
  )

  const debouncedLoadOptions = useMemo(() => debounce(delimitationOptions, 500), [delimitationOptions, 500])

  const customSpaceOption = (currentLayer.variable as ToolsAndOptionalResult).customSpace

  const formatOptionLabel = (item: DelimitationOption) => (
    <div style={{ display: 'flex', flexDirection: 'column', margin: '0.2rem 0 0.2rem 0' }}>
      <span>{item?.name}</span>
      <span style={{ color: '#a5a4a4', fontSize: '0.8rem' }}>
        {delimitationTypeLabel(item?.type)} {item?.subtitle ? ' > ' + item?.subtitle : ''}
      </span>
    </div>
  )

  return (
    <Wrapper>
      <div className='delimitation'>
        <AsyncSelect
          cacheOptions
          defaultOptions
          value={(() => {
            const currentSpace = currentLayer.space as SpaceDelimitationGeoStandardWithShape & {
              delimitationKey?: string | null
            }

            let optionType: DelimitationOption['type'] = currentLayer.space.delimitationType
            if (isHealthTeamTool && currentSpace.delimitationKey) {
              optionType = 'healthUnit'
            }

            if (isHealthProfessionalTool) {
              const parsedDelimitation = parseHealthProfessionalDelimitationKey(
                currentSpace.delimitationKey ?? null,
              )
              if (parsedDelimitation.scopeType === 'healthUnit') {
                optionType = 'healthUnit'
              } else if (parsedDelimitation.scopeType === 'healthTeam') {
                optionType = 'healthTeam'
              } else {
                optionType = 'municipality'
              }
            }

            return {
              id: currentSpace.delimitationId,
              key: currentSpace.delimitationKey ?? null,
              municipalityId: currentSpace.delimitationId,
              name: currentSpace.delimitationTitle!,
              type: optionType,
              shape: currentSpace.shape as Polygon | MultiPolygon,
            } as DelimitationOption
          })()}
          loadOptions={debouncedLoadOptions}
          menuPlacement='auto'
          getOptionValue={item => `${item.type}:${item.id}:${item.key ?? ''}`}
          formatOptionLabel={formatOptionLabel}
          noOptionsMessage={() => 'Sem resultados'}
          loadingMessage={() => 'Carregando...'}
          placeholder={
            isHealthProfessionalTool
              ? 'Selecione um município, unidade de saúde ou equipe de saúde'
              : isHealthTeamTool
                ? 'Selecione um município ou unidade de saúde'
                : 'Selecione um município'
          }
          onChange={handleSetDelimitationFromSelect}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: base => ({ ...base, zIndex: 9999 }),
            control: baseStyles => ({
              ...baseStyles,
              height: '40px',
              borderRadius: theme.borderRadius,
            }),
            menu: baseStyles => ({
              ...baseStyles,
              maxHeight: '200px',
              minHeight: 'auto',
            }),
            menuList: baseStyles => ({
              ...baseStyles,
              maxHeight: '200px',
              minHeight: 'auto',
            }),
          }}
        />
      </div>
      <div>
        {customSpaceOption?.map((option, index) => (
          <div key={index} className='children-option'>
            <FontAwesomeIcon icon={faCheckCircle} /> <p>{option}</p>
          </div>
        ))}
      </div>
    </Wrapper>
  )
}
